/** Browser-local campaign history. The old browser-wide flag is deliberately not migrated. */
export const OFFER_DAY_MS = 24 * 60 * 60 * 1000
export const LIMITED_TIME_OFFER_HISTORY_VERSION = 1
const KEY_PREFIX = 'limitedTimeSuperOffer:v1:'

export type LimitedTimeOfferTrigger = 'post-purchase' | 'download' | 'save' | 'export' | 'editor-exit'

export interface LimitedTimeOfferHistory {
  version: 1
  impressions: number
  dismissals: number
  lastShownAt: number | null
  finalWindowStartedAt: number | null
  acceptedAt: number | null
}

export function emptyLimitedTimeOfferHistory(): LimitedTimeOfferHistory {
  return {
    version: LIMITED_TIME_OFFER_HISTORY_VERSION,
    impressions: 0,
    dismissals: 0,
    lastShownAt: null,
    finalWindowStartedAt: null,
    acceptedAt: null,
  }
}

export function limitedTimeOfferHistoryKey(userId: string | number): string {
  return `${KEY_PREFIX}${encodeURIComponent(String(userId))}`
}

function isTimestamp(value: unknown): value is number | null {
  return value === null || (typeof value === 'number' && Number.isFinite(value) && value >= 0)
}

/** Reject malformed or contradictory history instead of letting it reset campaign limits. */
export function parseLimitedTimeOfferHistory(raw: string | null): LimitedTimeOfferHistory | null {
  if (raw === null) return emptyLimitedTimeOfferHistory()
  try {
    const value = JSON.parse(raw) as Partial<LimitedTimeOfferHistory> | null
    if (!value || value.version !== 1 ||
      !Number.isInteger(value.impressions) || value.impressions! < 0 ||
      !Number.isInteger(value.dismissals) || value.dismissals! < 0 ||
      value.dismissals! > value.impressions! ||
      !isTimestamp(value.lastShownAt) || !isTimestamp(value.finalWindowStartedAt) ||
      !isTimestamp(value.acceptedAt)) return null
    if ((value.impressions === 0) !== (value.lastShownAt === null)) return null
    if ((value.dismissals! >= 2) !== (value.finalWindowStartedAt !== null)) return null
    if (value.acceptedAt !== null && value.impressions === 0) return null
    return value as LimitedTimeOfferHistory
  } catch {
    return null
  }
}

export function readLimitedTimeOfferHistory(userId: string | number): LimitedTimeOfferHistory | null {
  try {
    return parseLimitedTimeOfferHistory(localStorage.getItem(limitedTimeOfferHistoryKey(userId)))
  } catch {
    return null
  }
}

export function writeLimitedTimeOfferHistory(userId: string | number, history: LimitedTimeOfferHistory): boolean {
  try {
    localStorage.setItem(limitedTimeOfferHistoryKey(userId), JSON.stringify(history))
    return true
  } catch {
    return false
  }
}

export function canShowLimitedTimeOffer(
  history: LimitedTimeOfferHistory,
  trigger: LimitedTimeOfferTrigger,
  now: number,
): boolean {
  if (!Number.isFinite(now) || now < 0 || history.acceptedAt !== null || history.impressions >= 4) return false
  if (history.finalWindowStartedAt !== null && now >= history.finalWindowStartedAt + 14 * OFFER_DAY_MS) return false
  const interval = trigger === 'post-purchase' ? OFFER_DAY_MS : 7 * OFFER_DAY_MS
  return history.lastShownAt === null || now - history.lastShownAt >= interval
}

export function recordLimitedTimeOfferImpression(history: LimitedTimeOfferHistory, now: number): LimitedTimeOfferHistory {
  return { ...history, impressions: history.impressions + 1, lastShownAt: now }
}

export function recordLimitedTimeOfferDismissal(history: LimitedTimeOfferHistory, now: number): LimitedTimeOfferHistory {
  const dismissals = history.dismissals + 1
  return {
    ...history,
    dismissals,
    finalWindowStartedAt: history.finalWindowStartedAt ?? (dismissals >= 2 ? now : null),
  }
}

export function recordLimitedTimeOfferAcceptance(history: LimitedTimeOfferHistory, now: number): LimitedTimeOfferHistory {
  return { ...history, acceptedAt: now }
}
